import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { userApi } from '../../services/userApi';
import styles from './AdminUsersDashboard.module.css';

interface User {
  User_ID: string;
  firstname: string;
  lastname: string;
  title?: string;
  mail: string;
  jobtitle?: string;
  picture?: string;
  created_at: string;
  updated_at: string;
  status?: number;
  firm?: {
    Firm_ID: string;
    abbrev: string;
    name?: string;
    logo?: string;
  };
}

const Admin_Users_Dashboard: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const isMountedRef = useRef<boolean>(true);

  const getErrorMessage = async (error: any): Promise<string> => {
    if (error?.response?.json) {
      try {
        const errorData = await error.response.json();
        return errorData.detail || errorData.message || 'An error occurred';
      } catch {
        return error.message || 'An error occurred';
      }
    } else if (error?.detail) {
      return error.detail;
    } else if (error?.message) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await userApi.getAllUsers();
      
      if (!Array.isArray(data)) {
        toast.error('Invalid data format received from the server', {
          position: "top-right",
          autoClose: 5000
        });
        setLoading(false);
        return;
      }
      
      setUsers(data);
      setFilteredUsers(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(user =>
        (user.firstname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.lastname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.mail || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.firm?.abbrev || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  // Apply status filter
  useEffect(() => {
    let filtered = users;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        (user.firstname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.lastname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.mail || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.firm?.abbrev || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(user => user.status === 1);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(user => user.status !== 1);
    }
    
    setFilteredUsers(filtered);
  }, [searchTerm, users, statusFilter]);

  const handleDeleteUser = async (userId: string) => {
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete this user?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await userApi.deleteUser(userId);
                setUsers(prevUsers => prevUsers.filter(user => String(user.User_ID) !== userId));
                setFilteredUsers(prevFiltered => prevFiltered.filter(user => String(user.User_ID) !== userId));
                toast.success('User deleted successfully', {
                  position: "top-right",
                  autoClose: 3000
                });
              } catch (error) {
                console.error('Error deleting user:', error);
                const errorMessage = await getErrorMessage(error);
                toast.error(`Failed to delete user: ${errorMessage}`, {
                  position: "top-right",
                  autoClose: 3000
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: styles.deleteConfirmationToast,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString()
      .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className={`container mt-5 ${styles.loadingContainer}`}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading users...</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">User Management</li>
          </ol>
        </nav>

        <div className="mb-4">
          <h2 className="mb-1">User Management</h2>
          <p className="text-muted mb-0">
            <i className="bi bi-people me-2"></i>
            Manage users and their profiles
          </p>
        </div>

        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <div className={styles.tableHeaderTop}>
              <h5 className="mb-0">Users Overview</h5>
              <div className={styles.headerActions}>
                <button 
                  className="btn btn-sm btn-outline-secondary me-2"
                  onClick={loadUsers}
                  title="Refresh user list"
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh
                </button>
                <Link to="/admin/users/create" className="btn btn-sm btn-primary">
                  <i className="bi bi-plus-circle me-1"></i>
                  Create User
                </Link>
              </div>
            </div>
          </div>
          
          <div className="p-3 border-bottom bg-white">
            <div className="input-group mb-3">
              <span className="input-group-text bg-white">
                <i className="bi bi-search"></i>
              </span>
              <input 
                type="text"
                className="form-control"
                placeholder="Search by name, email or firm..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="btn btn-outline-secondary" 
                  type="button"
                  onClick={() => setSearchTerm('')}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
            
            {/* Status Filter */}
            <div className="btn-group w-100" role="group">
              <button
                type="button"
                className={`btn ${statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setStatusFilter('all')}
              >
                <i className="bi bi-people me-1"></i>
                All Users
                <span className="badge bg-white text-primary ms-2">{users.length}</span>
              </button>
              <button
                type="button"
                className={`btn ${statusFilter === 'active' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => setStatusFilter('active')}
              >
                <i className="bi bi-check-circle me-1"></i>
                Active
                <span className={`badge ${statusFilter === 'active' ? 'bg-white text-success' : 'bg-success text-white'} ms-2`}>
                  {users.filter(u => u.status === 1).length}
                </span>
              </button>
              <button
                type="button"
                className={`btn ${statusFilter === 'inactive' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => setStatusFilter('inactive')}
              >
                <i className="bi bi-x-circle me-1"></i>
                Inactive
                <span className={`badge ${statusFilter === 'inactive' ? 'bg-white text-secondary' : 'bg-secondary text-white'} ms-2`}>
                  {users.filter(u => u.status !== 1).length}
                </span>
              </button>
            </div>
          </div>
          
          <div className={styles.desktopTable}>
            <table className={`table ${styles.userTable}`}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Firm</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center p-5">
                      <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#dee2e6' }}></i>
                      <p className="text-muted mt-3">
                        {searchTerm ? 'No users found matching your search' : 'No users available'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.User_ID} className={styles.tableRow}>
                      <td>
                        <div className={styles.userInfo}>
                          {user.picture ? (
                            <img 
                              src={`data:image/png;base64,${user.picture}`} 
                              alt={`${user.firstname} ${user.lastname}`}
                              className={styles.userPicture}
                            />
                          ) : (
                            <div className={styles.userPicturePlaceholder}>
                              <i className="bi bi-person-fill"></i>
                            </div>
                          )}
                          <div className={styles.userDetails}>
                            <Link 
                              to={`/admin/users/${user.User_ID}`}
                              className={styles.userName}
                            >
                              {user.title && <span className="text-muted me-1">{user.title}</span>}
                              {user.firstname} {user.lastname}
                              {user.status !== 1 && (
                                <span className="badge bg-secondary ms-2">Inactive</span>
                              )}
                            </Link>
                            <span className={styles.userEmail}>{user.mail}</span>
                            {user.jobtitle && (
                              <span className={styles.userJobtitle}>{user.jobtitle}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        {user.firm && (
                          <div className={styles.firmInfo}>
                            {user.firm.logo && (
                              <img 
                                src={`data:image/png;base64,${user.firm.logo}`} 
                                alt={user.firm.abbrev}
                                className={styles.firmLogo}
                              />
                            )}
                            <span>{user.firm.name || user.firm.abbrev}</span>
                          </div>
                        )}
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>{formatDate(user.updated_at)}</td>
                      <td>
                        <div className={styles.actionButtons}>
                          <Link 
                            to={`/admin/users/${user.User_ID}`}
                            className="btn btn-sm btn-outline-primary me-1"
                            title="Edit user"
                          >
                            <i className="bi bi-pencil"></i>
                          </Link>
                          <Link 
                            to={`/admin/users/${user.User_ID}/roles`}
                            className="btn btn-sm btn-outline-secondary me-1"
                            title="Manage roles"
                          >
                            <i className="bi bi-shield-lock"></i>
                          </Link>
                          <button 
                            onClick={() => handleDeleteUser(user.User_ID)}
                            className="btn btn-sm btn-outline-danger"
                            title="Delete user"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.mobileCards}>
            {filteredUsers.length === 0 ? (
              <div className={styles.emptyState}>
                <i className="bi bi-inbox"></i>
                <p>{searchTerm ? 'No users found matching your search' : 'No users available'}</p>
              </div>
            ) : (
              filteredUsers.map((user) => (
                <div key={user.User_ID} className={styles.userCard}>
                  <div className={styles.userCardHeader}>
                    <div className={styles.userInfo}>
                      {user.picture ? (
                        <img 
                          src={`data:image/png;base64,${user.picture}`} 
                          alt={`${user.firstname} ${user.lastname}`}
                          className={styles.userPictureMobile}
                        />
                      ) : (
                        <div className={styles.userPicturePlaceholderMobile}>
                          <i className="bi bi-person-fill"></i>
                        </div>
                      )}
                      <div className={styles.userDetails}>
                        <Link 
                          to={`/admin/users/${user.User_ID}`}
                          className={styles.userName}
                        >
                          {user.title && <span className="text-muted me-1">{user.title}</span>}
                          {user.firstname} {user.lastname}
                          {user.status !== 1 && (
                            <span className="badge bg-secondary ms-2">Inactive</span>
                          )}
                        </Link>
                        <span className={styles.userEmail}>{user.mail}</span>
                        {user.jobtitle && (
                          <span className={styles.userJobtitle}>{user.jobtitle}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.userCardBody}>
                    {user.firm && (
                      <div className={styles.userCardRow}>
                        <strong>Firm:</strong>
                        <div className={styles.firmInfo}>
                          {user.firm.logo && (
                            <img 
                              src={`data:image/png;base64,${user.firm.logo}`} 
                              alt={user.firm.abbrev}
                              className={styles.firmLogo}
                            />
                          )}
                          <span>{user.firm.name || user.firm.abbrev}</span>
                        </div>
                      </div>
                    )}
                    <div className={styles.userCardRow}>
                      <strong>Created:</strong>
                      <span>{formatDate(user.created_at)}</span>
                    </div>
                    <div className={styles.userCardRow}>
                      <strong>Updated:</strong>
                      <span>{formatDate(user.updated_at)}</span>
                    </div>
                  </div>
                  <div className={styles.userCardFooter}>
                    <Link 
                      to={`/admin/users/${user.User_ID}`}
                      className="btn btn-sm btn-outline-primary"
                    >
                      <i className="bi bi-pencil me-1"></i>
                      Edit
                    </Link>
                    <Link 
                      to={`/admin/users/${user.User_ID}/roles`}
                      className="btn btn-sm btn-outline-secondary"
                    >
                      <i className="bi bi-shield-lock me-1"></i>
                      Roles
                    </Link>
                    <button 
                      onClick={() => handleDeleteUser(user.User_ID)}
                      className="btn btn-sm btn-outline-danger"
                    >
                      <i className="bi bi-trash me-1"></i>
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin_Users_Dashboard;
